/* Local (offline) build of the daily / weekly derived report, mirroring backend/harmonized.py. */

const iso = (d) => d.toISOString().slice(0, 10);
const parse = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(Date.UTC(y, m - 1, d)); };
const addDays = (s, n) => { const d = parse(s); d.setUTCDate(d.getUTCDate() + n); return iso(d); };

export function epiWeek(s) {
  const d = parse(s);
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const y = d.getUTCFullYear();
  const start = new Date(Date.UTC(y, 0, 1));
  return [y, Math.ceil(((d - start) / 86400000 + 1) / 7)];
}

export function weekBounds(y, w) {
  const jan4 = new Date(Date.UTC(y, 0, 4));
  const day = jan4.getUTCDay() || 7;
  const mon = new Date(jan4);
  mon.setUTCDate(jan4.getUTCDate() - day + 1 + (w - 1) * 7);
  const a = iso(mon);
  return [a, addDays(a, 6)];
}

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const cleanNum = (x) => (Number.isInteger(x) ? x : Math.round(x * 10) / 10);
const fields = (fw) => fw.groups.flatMap((g) => g.fields);

export function aggregate(entries, fw) {
  const sorted = [...entries].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const out = {};
  for (const f of fields(fw)) {
    const vals = sorted.map((e) => (e.values || {})[f.code]).filter((v) => v !== undefined && v !== null && v !== "").map(num);
    if (!vals.length) { out[f.code] = 0; continue; }
    let v;
    if (f.agg === "sum") v = vals.reduce((s, x) => s + x, 0);
    else if (f.agg === "avg") v = vals.reduce((s, x) => s + x, 0) / vals.length;
    else if (f.agg === "max") v = Math.max(...vals);
    else v = vals[vals.length - 1];
    out[f.code] = cleanNum(v);
  }
  for (const d of fw.derived || []) {
    const den = num(out[d.den]);
    out[d.code] = den ? Math.round((num(out[d.num]) / den) * 1000) / 10 : null;
  }
  return out;
}

export function indicatorRows(agg, prev, fw) {
  const rows = fields(fw).map((f) => {
    const cur = agg[f.code];
    const pv = prev ? prev[f.code] : undefined;
    const change = pv ? Math.round(((num(cur) - num(pv)) / num(pv)) * 1000) / 10 : null;
    return { code: f.code, label: f.label, freq: f.freq, current: cur, previous: pv ?? null, change };
  });
  for (const d of fw.derived || []) {
    rows.push({ code: d.code, label: d.label, freq: "derived", current: agg[d.code], previous: prev ? prev[d.code] ?? null : null, change: null });
  }
  return rows;
}

export function narrativeGroups(entries, fw) {
  const sorted = [...entries].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  return (fw.narratives || []).map((n) => {
    const items = [];
    for (const e of sorted) {
      const txt = ((e.narratives || {})[n.code] || "").trim();
      if (txt) items.push({ date: e.date, author: e.author_name, text: txt, confidential: !!((e.confidential || {})[n.code]) });
    }
    return { code: n.code, label: n.label, items, merged: items.filter((i) => !i.confidential).map((i) => `[${i.date}] ${i.text}`).join("\n") };
  });
}

const AREA_NUM = ["alerts_received", "alerts_expected", "contacts_total", "contacts_seen"];
export function areasTable(entries) {
  const merged = {};
  for (const e of [...entries].sort((a, b) => (a.date || "").localeCompare(b.date || ""))) {
    for (const a of e.areas || []) {
      if (!a.name) continue;
      const row = merged[a.name] || (merged[a.name] = { name: a.name, observation: "", ...Object.fromEntries(AREA_NUM.map((k) => [k, 0])) });
      for (const k of AREA_NUM) if (a[k] !== "" && a[k] != null) row[k] += num(a[k]);
      if (a.observation) row.observation = `[${e.date}] ${a.observation}`;
    }
  }
  return Object.values(merged).map((r) => ({
    ...r,
    alert_rate: r.alerts_expected ? Math.round((r.alerts_received / r.alerts_expected) * 100) : null,
    contact_rate: r.contacts_total ? Math.round((r.contacts_seen / r.contacts_total) * 100) : null,
  }));
}

export function periodOf(ptype, day) {
  if (ptype === "daily") return { key: day, from: day, to: day, label: `Journée du ${day}`, body: { ptype, date: day } };
  const [y, w] = epiWeek(day);
  const [a, b] = weekBounds(y, w);
  return { key: `${y}-W${String(w).padStart(2, "0")}`, from: a, to: b, label: `Semaine épidémiologique S${String(w).padStart(2, "0")}/${y}`, body: { ptype, year: y, week: w } };
}

function previousPeriod(ptype, p) {
  if (ptype === "daily") { const d = addDays(p.from, -1); return [d, d]; }
  const [y, w] = epiWeek(p.from);
  return w > 1 ? weekBounds(y, w - 1) : weekBounds(y - 1, 52);
}

/** Builds the full report document on the device; `entries` must already include today's pending edits. */
export function buildLocalReport(ptype, day, fw, entries, user) {
  const p = periodOf(ptype, day);
  const inRange = (e, a, b) => e.volet === fw.volet && e.date >= a && e.date <= b;
  const cur = entries.filter((e) => inRange(e, p.from, p.to));
  if (!cur.length) return null;
  const [pa, pb] = previousPeriod(ptype, p);
  const prevE = entries.filter((e) => inRange(e, pa, pb));
  const agg = aggregate(cur, fw);
  const prev = prevE.length ? aggregate(prevE, fw) : null;
  const groups = narrativeGroups(cur, fw);
  return {
    id: `local:${ptype}:${fw.volet}:${p.key}`, local: true,
    ptype, volet: fw.volet, period_key: p.key, period_label: p.label,
    scope_label: user?.health_zone || user?.province || user?.name || "",
    period: { from: p.from, to: p.to },
    indicators: indicatorRows(agg, prev, fw), aggregates: agg, narrative_groups: groups, areas: areasTable(cur),
    activities: cur.flatMap((e) => (e.activities || []).map((a) => ({ ...a, date: e.date }))),
    challenges: cur.flatMap((e) => (e.challenges || []).map((c) => ({ ...c, date: e.date }))),
    narratives: Object.fromEntries(groups.map((g) => [g.code, g.merged])),
    photos: cur.flatMap((e) => (e.photos || []).map((ph) => ({ ...ph, date: e.date, author_name: e.author_name }))).slice(0, 12),
    entries_count: cur.length, author_name: user?.name, status: "draft",
    generated_at: new Date().toISOString(), body: { ...p.body, volet: fw.volet },
  };
}
